import React, { useEffect } from 'react'
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { chevronBack, chevronForward } from 'ionicons/icons'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import {
  saveFillablePDF,
  resetFillablePDFsState,
  setCurrentStep,
  initializeFillablePDF,
} from '../store/fillablePDFs.actions'
import Stepper from '../../../components/stepper/Stepper.component'
import Logger from '../../../services/logger.service'
import { goBack } from '../../../utilities/navigation.util'
import './fillablePDFs.page.css'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { AuthenticationService } from '../../login/services/authentication.service'
import { getUserSelector } from '../../user/selectors'
import { useHistory } from 'react-router'
import { getFillablePDFsSelector } from '../selectors'
import { showToast } from '../../../components/toast/store/toast.actions'
import { FILLABLE_PDFS_STEPS, STEP_IDS } from '../constants'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import FillablePDFsContent from '../fillablePDFsContent/FillablePDFs.content'

const FillablePDFs: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const fillablePDFsState = useSelector(getFillablePDFsSelector)
  const { __currentStep } = fillablePDFsState
  const history = useHistory()
  const user = useSelector(getUserSelector)

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)

  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes, history])

  useIonViewWillEnter(() => {
    dispatch(initializeFillablePDF())
  })

  useIonViewDidLeave(() => {
    dispatch(resetFillablePDFsState())
  })

  const onSaveClick = () => {
    dispatch(saveFillablePDF())
    dispatch(
      showToast({
        message: t('fillablePDFs.saveSuccess'),
        position: 'top',
        color: ToastColor.Success,
      })
    )
    goBack()
  }

  const onStepClick = (stepId: number) => {
    dispatch(setCurrentStep(stepId))
  }

  const onCancel = () => {
    const alertState = {
      header: t('fillablePDFs.exitConfirmationHeader'),
      message: t('fillablePDFs.exitConfirmationMessage'),
      buttons: [
        { text: t('common.buttons.Cancel') },
        {
          text: t('common.buttons.Continue'),
          role: 'destructive',
          handler: () => {
            Logger.info(t('fillablePDFs.userCancelled'))
            goBack()
          },
        },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const handlePrevious = () => {
    if (__currentStep > STEP_IDS.DETAILS) {
      dispatch(setCurrentStep(__currentStep - 1))
    } else {
      onCancel()
    }
  }

  const handleNext = () => {
    if (__currentStep < FILLABLE_PDFS_STEPS.length) {
      dispatch(setCurrentStep(__currentStep + 1))
    } else {
      onSaveClick()
    }
  }

  const renderFooter = () => {
    const isLastStep = __currentStep === FILLABLE_PDFS_STEPS.length
    const nextMessage = isLastStep ? 'fillablePDFs.saveAndAttach' : 'fillablePDFs.review'
    const previousMessage = isLastStep ? 'fillablePDFs.modify' : 'fillablePDFs.changeForm'

    return (
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10 footer-toolbar-padding'>
          <IonButtons slot='start'>
            <IonButton data-testid='Cancel' onClick={onCancel} expand='block' className='p-5 btn btn-secondary mr-8'>
              {t('common.buttons.Cancel')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end' className='gap-x-2'>
            <IonButton data-testid='Back' className='pr-5 btn btn-secondary' expand='block' onClick={handlePrevious}>
              <IonIcon className='special-reports-footer-button-chevron' icon={chevronBack} />
              {t(previousMessage)}
            </IonButton>
            <IonButton
              data-testid='Next'
              className={`pl-5 btn btn-primary ${isLastStep ? 'pr-5' : ''}`}
              expand='block'
              onClick={handleNext}>
              {t(nextMessage)}
              {isLastStep ? null : <IonIcon className='special-reports-footer-button-chevron' icon={chevronForward} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    )
  }

  const renderHeader = () => {
    return (
      <>
        <PCRHeader />
        <IonHeader className='ion-no-padding'>
          <IonToolbar color='light'>
            <IonTitle>{fillablePDFsState.name}</IonTitle>
          </IonToolbar>
          <IonToolbar color='light'>
            <Stepper steps={FILLABLE_PDFS_STEPS} currentStep={__currentStep} onStepClick={onStepClick} />
          </IonToolbar>
        </IonHeader>
      </>
    )
  }

  return (
    <IonPage id='FillablePDFsPage'>
      {renderHeader()}
      <FillablePDFsContent />
      {renderFooter()}
    </IonPage>
  )
}

export default FillablePDFs
